import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import {
  FaArrowRight,
  FaChevronDown,
  FaEnvelope,
  FaExternalLinkAlt,
  FaPaperPlane,
} from 'react-icons/fa';
import { products } from '../data/productsData';
import logoImage from '../assets/logo.png';
import { db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';

const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie';

const QUICK_PROMPTS = [
  'What does Trend Genie app do?',
  'Explain RugShield in simple words',
  'How much does the app cost?',
  'What is TGEN used for?',
  'Which product should I start with?',
  'How do I reduce trading risk?',
];

const PRODUCT_ALIASES = {
  'android-app': ['trend genie', 'ai analysis', 'signals app', 'android app', 'crypto signals app'],
  'rugshield-web': ['rugshield', 'defi scanner', 'honeypot', 'rug pull', 'token safety', 'scanner'],
  social: ['geniesocials', 'genie social', 'social', 'community', 'tgen rewards'],
  nova: ['genienova', 'nova', 'research workspace', 'ai research'],
};

const PRODUCT_ANCHORS = {
  'android-app': ['trend genie', 'android app', 'signals app', 'crypto signals'],
  'rugshield-web': ['rugshield', 'defi scanner', 'honeypot', 'rug pull'],
  social: ['geniesocials', 'genie social', 'trend genie social'],
  nova: ['genienova', 'trend genie nova', 'nova', 'research workspace'],
};

const DIRECT_INTENTS = [
  {
    id: 'market_price',
    patterns: [
      'price of btc right now',
      'price of btc',
      'btc price',
      'bitcoin price',
      'current price of btc',
      'current bitcoin price',
      'live btc price',
      'price of eth',
      'eth price',
      'ethereum price',
      'current price',
      'live price',
      'coin price',
    ],
    answer: {
      title: 'Live Market Price',
      text:
        'I cannot provide a real-time BTC or coin price inside this website assistant right now because it is not connected to a live market data feed. For live market tracking and broader crypto workflow support, use Trend Genie: AI Analysis alongside your exchange or charting platform.',
      links: [
        { label: 'Trend Genie App Page', href: '/ai-crypto-signals-app', external: false },
        { label: 'Download on Google Play', href: PLAY_STORE_URL, external: true },
      ],
      suggestions: [
        'What does Trend Genie app do?',
        'How do signals work?',
        'How can I get the app?',
      ],
    },
  },
  {
    id: 'greeting',
    patterns: [
      'hello',
      'hi',
      'hey',
      'hello there',
      'hey there',
      'hi there',
      'assalam',
      'aoa',
      'salam',
      'how are you',
      'hello there how are you',
    ],
    answer: {
      title: 'Genie',
      text:
        'I am ready to help. Ask me about Trend Genie: AI Analysis, RugShield, GenieSocials, GenieNova, pricing, rewards, onboarding, or crypto basics.',
      suggestions: [
        'What does Trend Genie app do?',
        'Is the app free?',
        'What is RugShield?',
      ],
    },
  },
  {
    id: 'identity',
    patterns: [
      'are you ai',
      'are you an ai',
      'are you a bot',
      'who are you',
      'what are you',
      'what can you do',
      'what do you do',
    ],
    answer: {
      title: 'About This Assistant',
      text:
        'Yes. I am the Trend Genie AI Assistant for this website. I answer questions about Trend Genie: AI Analysis, RugShield, GenieSocials, GenieNova, pricing, rewards, onboarding, and crypto basics.',
      suggestions: [
        'What does Trend Genie app do?',
        'What is RugShield?',
        'Which products are live?',
      ],
    },
  },
  {
    id: 'thanks',
    patterns: ['thanks', 'thank you', 'shukriya', 'jazakallah'],
    answer: {
      title: 'You Are Welcome',
      text:
        'You can continue by asking about the app, RugShield, GenieSocials, GenieNova, TGEN, pricing, or crypto basics.',
      suggestions: ['Is the app free?', 'What is RugShield?', 'What is TGEN used for?'],
    },
  },
  {
    id: 'app_free',
    patterns: ['is trend genie android app free', 'is the app free', 'is trend genie free', 'is this app free'],
    answer: {
      title: 'App Pricing',
      text:
        'No. Trend Genie: AI Analysis is not permanently free. It starts with a 7-day free trial, then continues at $10 per month.',
      links: [
        { label: 'AI Signals App Page', href: '/ai-crypto-signals-app', external: false },
        { label: 'Download on Google Play', href: PLAY_STORE_URL, external: true },
      ],
      suggestions: ['What features are in the app?', 'How do I get the app?'],
    },
  },
  {
    id: 'app_no_plan',
    patterns: [
      'if i dont buy your monthly plan then what will happen',
      'if i dont buy the monthly plan then what will happen',
      'what happens if i dont buy the monthly plan',
      'what happens after the free trial',
      'what happens after trial',
      'if i do not buy the monthly plan',
      'if i dont subscribe then what happens',
      'if i dont pay after trial',
      'after trial what happens',
    ],
    answer: {
      title: 'After the Free Trial',
      text:
        'After the 7-day free trial ends, the paid app features continue only if you subscribe to the $10 per month plan. If you do not subscribe, you should expect access to the full premium app experience to stop, while free-tier ecosystem features may remain limited compared with premium access.',
      links: [
        { label: 'AI Signals App Page', href: '/ai-crypto-signals-app', external: false },
        { label: 'Download on Google Play', href: PLAY_STORE_URL, external: true },
      ],
      suggestions: ['How much does the app cost?', 'What features are in the app?'],
    },
  },
  {
    id: 'get_app',
    patterns: ['how can i get this app', 'how do i get this app', 'how can i get the app', 'how do i get the app', 'where can i download the app'],
    answer: {
      title: 'How to Get the App',
      text:
        'Download Trend Genie: AI Analysis from Google Play, create your account, and start with the 7-day free trial.',
      links: [
        { label: 'Download on Google Play', href: PLAY_STORE_URL, external: true },
        { label: 'AI Signals App Page', href: '/ai-crypto-signals-app', external: false },
      ],
      suggestions: ['Is the app free?', 'What does the app do?'],
    },
  },
  {
    id: 'app_features',
    patterns: [
      'what features are in the app',
      'what features does the app have',
      'what are the app features',
      'what can the app do',
      'what does the app include',
      'how do signals work',
    ],
    answer: {
      title: 'App Features',
      text:
        'Trend Genie: AI Analysis includes real-time crypto trade setups with entry, TP1, TP2, and stop-loss, plus chart-image analysis, built-in AI coin analysis, crypto news, watchlists, and trader discussion features.',
      links: [
        { label: 'AI Signals App Page', href: '/ai-crypto-signals-app', external: false },
        { label: 'Download on Google Play', href: PLAY_STORE_URL, external: true },
      ],
      suggestions: ['Is the app free?', 'What happens after 7 days?', 'How do I get the app?'],
    },
  },
  {
    id: 'signal_count',
    patterns: [
      'how many signals can i get',
      'how many signals do i get',
      'how many signals will i get',
      'how many signals are there',
      'how often do signals come',
      'how many trade signals',
    ],
    answer: {
      title: 'Genie',
      text:
        'There is no fixed daily number. Trend Genie: AI Analysis generates signals when the system finds valid market setups, so signal frequency depends on real market conditions rather than an artificial quota.',
      links: [
        { label: 'AI Signals App Page', href: '/ai-crypto-signals-app', external: false },
        { label: 'Live Results', href: '/live-results', external: false },
      ],
      suggestions: ['How do signals work?', 'Is the app free?', 'What features are in the app?'],
    },
  },
  {
    id: 'signal_accuracy',
    patterns: [
      'what is the accuracy',
      'what is your accuracy',
      'how accurate are the signals',
      'signal accuracy',
      'win rate',
      'success rate',
      'how accurate is the app',
    ],
    answer: {
      title: 'Genie',
      text:
        'Trend Genie positions its tracked signal performance around an 83%+ win rate on tracked signals. Signal outcomes still depend on market conditions and risk management, so no setup should be treated as guaranteed.',
      links: [
        { label: 'AI Signals App Page', href: '/ai-crypto-signals-app', external: false },
        { label: 'Live Results', href: '/live-results', external: false },
      ],
      suggestions: ['How do signals work?', 'How should I manage risk?', 'Is the app free?'],
    },
  },
  {
    id: 'app_what_is',
    patterns: ['what does trend genie app do', 'what does the app do', 'what is trend genie app', 'what is this app'],
    answer: {
      title: 'What the App Does',
      text:
        'Trend Genie: AI Analysis scans the crypto market in real time and shows structured trade setups with entry price, TP1, TP2, and stop-loss. It also includes chart-image analysis, built-in AI coin analysis, crypto news, and trader discussion features.',
      links: [{ label: 'AI Signals App Page', href: '/ai-crypto-signals-app', external: false }],
      suggestions: ['Is the app free?', 'How do signals work?'],
    },
  },
  {
    id: 'rugshield_what_is',
    patterns: ['what is rugshield', 'explain rugshield', 'what does rugshield do', 'what does rugshield check'],
    answer: {
      title: 'What RugShield Does',
      text:
        'RugShield is a free read-only DeFi safety scanner. It helps detect honeypots, rug-pull risks, ownership red flags, and other contract-level warnings on Ethereum and BNB Chain before you buy a token.',
      links: [
        { label: 'RugShield Page', href: '/rugshield-defi-scanner', external: false },
        { label: 'Open RugShield', href: 'https://app.trendgenie.io', external: true },
      ],
      suggestions: ['Is RugShield free?', 'How does RugShield work?'],
    },
  },
  {
    id: 'rugshield_free',
    patterns: ['is rugshield free', 'is the rugshield scanner free', 'does rugshield cost money'],
    answer: {
      title: 'RugShield Pricing',
      text:
        'Yes. RugShield is free to use and does not require a wallet connection for basic token scans.',
      links: [{ label: 'RugShield Page', href: '/rugshield-defi-scanner', external: false }],
      suggestions: ['What does RugShield check?', 'How does RugShield work?'],
    },
  },
  {
    id: 'social_what_is',
    patterns: ['what is geniesocials', 'what is trend genie social', 'what is social'],
    answer: {
      title: 'What GenieSocials Is',
      text:
        'GenieSocials is the community layer of the ecosystem. It is built for discussion, shared market insights, and TGEN-linked rewards through ecosystem participation.',
      links: [{ label: 'Social Product Page', href: '/products/social', external: false }],
      suggestions: ['Is GenieSocials free?', 'What is TGEN used for?'],
    },
  },
  {
    id: 'social_free',
    patterns: ['is geniesocials free', 'is social free', 'is trend genie social free'],
    answer: {
      title: 'GenieSocials Access',
      text:
        'Yes. GenieSocials is free to join. It is positioned around community participation, discussion, and TGEN-linked ecosystem rewards.',
      links: [{ label: 'Social Product Page', href: '/products/social', external: false }],
      suggestions: ['What is TGEN used for?', 'How do rewards work?'],
    },
  },
  {
    id: 'nova_what_is',
    patterns: ['what is genienova', 'what is nova', 'what does genienova do'],
    answer: {
      title: 'What GenieNova Is',
      text:
        'GenieNova is the advanced AI research and market-intelligence layer of the Trend Genie ecosystem. It is positioned for deeper analysis and is currently in testing.',
      links: [{ label: 'GenieNova Page', href: '/products/nova', external: false }],
      suggestions: ['Is GenieNova live?', 'Which product should I start with?'],
    },
  },
  {
    id: 'nova_live',
    patterns: ['is genienova live', 'is nova live', 'has genienova launched', 'is nova launched'],
    answer: {
      title: 'GenieNova Status',
      text:
        'No. GenieNova is currently in testing and should be treated as an emerging product rather than a fully public live release.',
      links: [{ label: 'GenieNova Page', href: '/products/nova', external: false }],
      suggestions: ['What is GenieNova?', 'Which products are live?'],
    },
  },
  {
    id: 'tgen_what_is',
    patterns: ['what is tgen', 'what is the tgen token', 'what is tgen used for'],
    answer: {
      title: 'TGEN Utility',
      text:
        'TGEN is the ecosystem utility token used around rewards, staking, quests, referrals, and broader participation, especially within GenieSocials and the wider Trend Genie ecosystem.',
      links: [
        { label: '$TGEN Token', href: '/tgen-token', external: false },
        { label: 'Rewards', href: '/tgen-airdrop-live', external: false },
      ],
      suggestions: ['What is GenieSocials?', 'How do rewards work?'],
    },
  },
  {
    id: 'rewards',
    patterns: ['how do rewards work', 'how can i earn tgen', 'how do i earn tgen', 'what are rewards', 'how do i get rewards'],
    answer: {
      title: 'Rewards and TGEN',
      text:
        'Rewards are tied to ecosystem participation, especially through GenieSocials, referrals, quests, and TGEN-linked activity. For current public reward details, open the rewards page below.',
      links: [{ label: 'Rewards Page', href: '/tgen-airdrop-live', external: false }],
      suggestions: ['What is TGEN used for?', 'What is GenieSocials?'],
    },
  },
  {
    id: 'products_live',
    patterns: ['which products are live', 'what products are live', 'which trend genie products are live'],
    answer: {
      title: 'Current Product Status',
      text:
        'Live products are Trend Genie: AI Analysis, RugShield, and GenieSocials. GenieNova is currently in testing.',
      links: [{ label: 'View All Products', href: '/products', external: false }],
      suggestions: ['Which product should I start with?', 'What does RugShield do?'],
    },
  },
  {
    id: 'support_direct',
    patterns: ['contact support', 'support email', 'how can i contact support', 'how do i contact support'],
    answer: {
      title: 'Support Contact',
      text:
        'For direct support, email support@trendgenie.io. You can also leave your details in the support form inside this assistant if you want a follow-up.',
      links: [{ label: 'Email Support', href: 'mailto:support@trendgenie.io', external: true }],
      suggestions: ['How much does the app cost?', 'Which products are live?'],
      leadCapture: true,
    },
  },
  {
    id: 'login_issue',
    patterns: [
      'i am not able to sign in',
      'i cannot sign in',
      'cant sign in',
      'cannot login',
      'cant login',
      'invalid user name or password',
      'invalid username or password',
      'login issue',
      'sign in issue',
      'password issue',
      'unable to login',
    ],
    answer: {
      title: 'Login Help',
      text:
        'This sounds like an account sign-in issue. First, recheck your username or email and password, then try the correct sign-in method again. If the error continues, contact support@trendgenie.io so the team can review your account directly.',
      links: [
        { label: 'Email Support', href: 'mailto:support@trendgenie.io', external: true },
        { label: 'AI Signals App Page', href: '/ai-crypto-signals-app', external: false },
      ],
      suggestions: ['How can I contact support?', 'How do I get the app?', 'What happens after 7 days?'],
      leadCapture: true,
    },
  },
];

const TOPICS = [
  {
    id: 'pricing',
    keywords: ['pricing', 'price', 'cost', 'monthly', 'subscription', 'trial', 'free', 'paid'],
    build: () => ({
      title: 'Pricing Overview',
      text:
        'Trend Genie: AI Analysis offers a 7-day free trial and then continues at $10 per month. RugShield is free to use. Trend Genie Social is free to join, while Nova is currently in testing.',
      links: [
        { label: 'AI Signals App', href: '/ai-crypto-signals-app', external: false },
        { label: 'RugShield', href: '/rugshield-defi-scanner', external: false },
      ],
      suggestions: ['What features are in the app?', 'Is RugShield completely free?'],
    }),
  },
  {
    id: 'start',
    keywords: ['start', 'begin', 'new user', 'onboarding', 'which product', 'where should i start'],
    build: () => ({
      title: 'How to Get Started',
      text:
        'To get the app, download Trend Genie: AI Analysis from Google Play, create your account, start with the 7-day free trial, and then continue with the product workflow that fits you best. If you trade DeFi tokens, use RugShield before buying. Then use GenieSocials for discussion and rewards, and move to GenieNova for deeper research.',
      links: [
        { label: 'Open Products', href: '/products', external: false },
        { label: 'Download App', href: PLAY_STORE_URL, external: true },
      ],
      suggestions: ['How much does the app cost?', 'What features are in the app?'],
    }),
  },
  {
    id: 'signals',
    keywords: ['signal', 'signals', 'entry', 'tp', 'take profit', 'stop loss', 'win rate', 'accuracy'],
    build: () => ({
      title: 'AI Signals App',
      text:
        'Trend Genie: AI Analysis scans the crypto market in real time and shows structured trade setups with entry price, TP1, TP2, and stop-loss. It is designed to reduce guesswork and make signals easier to understand for both newer and experienced traders.',
      links: [
        { label: 'AI Signals App Page', href: '/ai-crypto-signals-app', external: false },
        { label: 'Live Results', href: '/live-results', external: false },
      ],
      suggestions: ['How much does the app cost?', 'How should I manage risk?'],
    }),
  },
  {
    id: 'tgen',
    keywords: ['tgen', 'token', 'staking', 'rewards', 'airdrop', 'quest'],
    build: () => ({
      title: 'TGEN Utility',
      text:
        'TGEN is the utility token tied to the Trend Genie ecosystem. It is positioned around rewards, staking, quests, referrals, and broader ecosystem participation, especially inside GenieSocials.',
      links: [
        { label: '$TGEN Token', href: '/tgen-token', external: false },
        { label: 'Rewards', href: '/tgen-airdrop-live', external: false },
      ],
      suggestions: ['What is GenieSocials?', 'How do rewards work?'],
    }),
  },
  {
    id: 'safety',
    keywords: ['risk', 'risk management', 'safe', 'safety', 'honeypot', 'rug pull', 'avoid loss'],
    build: () => ({
      title: 'Safer Trading Workflow',
      text:
        'Use the app for setup discovery, RugShield for contract checks, and disciplined position sizing before entering any trade. Never risk more than you can afford to lose, always respect stop-loss levels, and do not treat any tool as guaranteed profit.',
      links: [
        { label: 'RugShield', href: '/rugshield-defi-scanner', external: false },
        { label: 'Risk Insight', href: '/insights/risk-management-small-trading-accounts', external: false },
      ],
      suggestions: ['Explain RugShield in simple words', 'What does the app do?'],
    }),
  },
  {
    id: 'support',
    keywords: ['support', 'contact', 'email', 'issue', 'problem', 'bug', 'help me'],
    build: () => ({
      title: 'Support Options',
      text:
        'For product issues, account questions, or feature requests, the fastest direct contact is support@trendgenie.io. If you want, you can also leave your details below and the team can review your question.',
      links: [{ label: 'Email Support', href: 'mailto:support@trendgenie.io', external: true }],
      suggestions: ['How much does the app cost?', 'What products are live right now?'],
      leadCapture: true,
    }),
  },
];

function normalize(text) {
  return (text || '')
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function tokenize(text) {
  return normalize(text).split(' ').filter(Boolean);
}

function scoreMatch(question, keywords) {
  const q = normalize(question);
  const tokens = tokenize(question);
  let score = 0;

  keywords.forEach((keyword) => {
    const normalized = normalize(keyword);
    if (!normalized) return;
    if (q.includes(normalized)) {
      score += normalized.split(' ').length > 1 ? 4 : 2;
      return;
    }
    const overlap = normalized.split(' ').filter((part) => tokens.includes(part)).length;
    score += overlap;
  });

  return score;
}

const APP_REFERENCE_TERMS = [
  'trend genie',
  'your app',
  'this app',
  'android app',
  'signals app',
  'app',
];

function hasAnyPhrase(text, phrases) {
  return phrases.some((phrase) => text.includes(normalize(phrase)));
}

function hasAllConcepts(text, concepts) {
  return concepts.every((conceptGroup) => conceptGroup.some((term) => text.includes(normalize(term))));
}

function createAnswer({ title, text, links = [], suggestions = [], leadCapture = false }) {
  return { title, text, links, suggestions, leadCapture };
}

function formatLiveStatsAnswer(stats) {
  if (!stats) return null;

  const wins = (stats.tp1Hits || 0) + (stats.tp2Hits || 0);
  const totalResolved = wins + (stats.slHits || 0);
  const winRate = totalResolved > 0 ? Math.round((wins / totalResolved) * 100) : 0;

  return createAnswer({
    title: 'Genie',
    text:
      `Current live results show ${winRate}% win rate, ${Number(stats.totalSignals || 0).toLocaleString()} total signals fired, ${Number(stats.tp1Hits || 0).toLocaleString()} TP1 hits, ${Number(stats.tp2Hits || 0).toLocaleString()} TP2 hits, ${Number(stats.slHits || 0).toLocaleString()} stop-loss hits, ${Number(stats.active || 0).toLocaleString()} active signals, and +${Number(stats.growth || 0).toFixed(1)}% portfolio growth.`,
    links: [
      { label: 'Live Results', href: '/live-results', external: false },
      { label: 'AI Signals App Page', href: '/ai-crypto-signals-app', external: false },
    ],
    suggestions: ['How do signals work?', 'How many signals can I get?', 'Is the app free?'],
  });
}

function getDirectIntentAnswer(question) {
  const q = normalize(question);
  const match = DIRECT_INTENTS.find((intent) => {
    if (intent.id === 'greeting') {
      return intent.patterns.some((pattern) => q === normalize(pattern));
    }
    return intent.patterns.some((pattern) => q.includes(normalize(pattern)));
  });
  return match ? match.answer : null;
}

function getLiveStatsAnswer(question, stats) {
  const q = normalize(question);
  const asksStats = hasAnyPhrase(q, [
    'accuracy',
    'accurate',
    'win rate',
    'success rate',
    'stats',
    'statistics',
    'performance',
    'results',
    'how many tp1',
    'how many tp2',
    'how many active signals',
    'portfolio growth',
    'how many stop loss',
  ]);

  if (!asksStats) return null;
  return formatLiveStatsAnswer(stats);
}

function getBroadIntentAnswer(question) {
  const q = normalize(question);

  const mentionsApp = hasAnyPhrase(q, APP_REFERENCE_TERMS);
  const mentionsAuth = hasAnyPhrase(q, [
    'login',
    'log in',
    'sign in',
    'signup',
    'sign up',
    'register',
    'account',
    'password',
    'username',
    'email',
  ]);
  if (mentionsAuth && mentionsApp) {
    if (hasAnyPhrase(q, ['invalid', 'wrong password', 'wrong username', 'cannot sign in', 'cant sign in', 'unable to login', 'not able to sign in'])) {
      return createAnswer({
        title: 'Login Help',
        text:
          'This looks like a login or account-access issue. Recheck your email or username, confirm the password, and try the correct sign-in method again. If the problem continues, contact support@trendgenie.io so the team can check your account directly.',
        links: [{ label: 'Email Support', href: 'mailto:support@trendgenie.io', external: true }],
        suggestions: ['How can I contact support?', 'How do I get the app?', 'What happens after 7 days?'],
        leadCapture: true,
      });
    }

    if (hasAnyPhrase(q, ['sign up', 'signup', 'register', 'create account', 'make account', 'open account'])) {
      return createAnswer({
        title: 'Sign Up',
        text:
          'To get started, download Trend Genie: AI Analysis from Google Play, open the app, create your account, and begin with the 7-day free trial.',
        links: [
          { label: 'Download on Google Play', href: PLAY_STORE_URL, external: true },
          { label: 'AI Signals App Page', href: '/ai-crypto-signals-app', external: false },
        ],
        suggestions: ['Is the app free?', 'What features are in the app?', 'What happens after 7 days?'],
      });
    }
  }

  if (
    mentionsApp &&
    hasAnyPhrase(q, ['feature', 'features', 'include', 'included', 'what can it do', 'how do signals work', 'tools inside the app'])
  ) {
    return createAnswer({
      title: 'App Features',
      text:
        'Trend Genie: AI Analysis includes real-time crypto trade setups with entry, TP1, TP2, and stop-loss, plus chart-image analysis, built-in AI coin analysis, crypto news, watchlists, and trader discussion features.',
      links: [
        { label: 'AI Signals App Page', href: '/ai-crypto-signals-app', external: false },
        { label: 'Download on Google Play', href: PLAY_STORE_URL, external: true },
      ],
      suggestions: ['Is the app free?', 'What happens after 7 days?', 'How do I get the app?'],
    });
  }

  if (
    mentionsApp &&
    hasAnyPhrase(q, ['price', 'pricing', 'cost', 'monthly', 'subscription', 'plan', 'free', 'trial', 'paid'])
  ) {
    if (
      hasAnyPhrase(q, ['after 7 days', 'after seven days', 'after trial', 'after free trial']) ||
      hasAnyPhrase(q, ['if i dont buy', 'if i do not buy', 'if i dont subscribe', 'if i dont pay', 'without subscription'])
    ) {
      return createAnswer({
        title: 'After the Free Trial',
        text:
          'After the 7-day free trial ends, the full premium app experience continues only on the $10 per month plan. If you do not subscribe, premium access should stop and only more limited ecosystem access may remain.',
        links: [{ label: 'AI Signals App Page', href: '/ai-crypto-signals-app', external: false }],
        suggestions: ['What features are in the app?', 'How much does the app cost?', 'How do I get the app?'],
      });
    }

    if (hasAnyPhrase(q, ['free tier', 'free version', 'what is free', 'what can i use for free', 'free features'])) {
      return createAnswer({
        title: 'Free Tier vs Premium',
        text:
          'Trend Genie: AI Analysis starts with a 7-day free trial. After that, the full premium app experience requires the $10 per month plan. Outside premium access, you should expect more limited free-tier ecosystem access instead of the complete app feature set.',
        links: [{ label: 'AI Signals App Page', href: '/ai-crypto-signals-app', external: false }],
        suggestions: ['What happens after 7 days?', 'What features are in the app?', 'How much does the app cost?'],
      });
    }

    return createAnswer({
      title: 'App Pricing',
      text:
        'Trend Genie: AI Analysis starts with a 7-day free trial and then continues at $10 per month.',
      links: [
        { label: 'AI Signals App Page', href: '/ai-crypto-signals-app', external: false },
        { label: 'Download on Google Play', href: PLAY_STORE_URL, external: true },
      ],
      suggestions: ['What features are in the app?', 'What happens after 7 days?', 'How do I get the app?'],
    });
  }

  if (hasAnyPhrase(q, ['tool', 'tools', 'products', 'product', 'ecosystem'])) {
    return createAnswer({
      title: 'Trend Genie Tools',
      text:
        'The main tools are Trend Genie: AI Analysis, RugShield, GenieSocials, and GenieNova. The app is the main mobile product, RugShield handles DeFi safety scanning, GenieSocials focuses on community and rewards, and GenieNova is the advanced research layer currently in testing.',
      links: [{ label: 'View All Products', href: '/products', external: false }],
      suggestions: ['Which product should I start with?', 'What is RugShield?', 'What is TGEN used for?'],
    });
  }

  if (hasAnyPhrase(q, ['tgen', 'token', 'staking', 'rewards', 'airdrop', 'quest'])) {
    return createAnswer({
      title: 'TGEN Utility',
      text:
        'TGEN is the ecosystem utility token used around rewards, staking, quests, referrals, and broader participation, especially within GenieSocials and the wider Trend Genie ecosystem.',
      links: [
        { label: '$TGEN Token', href: '/tgen-token', external: false },
        { label: 'Rewards', href: '/tgen-airdrop-live', external: false },
      ],
      suggestions: ['How do rewards work?', 'What is GenieSocials?', 'Which products are live?'],
    });
  }

  if (
    hasAnyPhrase(q, ['btc', 'bitcoin', 'eth', 'ethereum', 'coin price', 'crypto price', 'live price', 'current price']) &&
    hasAnyPhrase(q, ['price', 'live', 'current', 'right now'])
  ) {
    return createAnswer({
      title: 'Live Market Price',
      text:
        'I cannot provide real-time BTC or coin prices inside this website assistant because it is not connected to a live market feed. For accurate live prices, use your exchange, TradingView, or CoinMarketCap. For broader crypto workflow support, use Trend Genie: AI Analysis alongside your charting tools.',
      links: [
        { label: 'Trend Genie App Page', href: '/ai-crypto-signals-app', external: false },
        { label: 'Download on Google Play', href: PLAY_STORE_URL, external: true },
      ],
      suggestions: ['What does Trend Genie app do?', 'How do signals work?', 'How can I get the app?'],
    });
  }

  if (hasAnyPhrase(q, ['hello', 'hi', 'hey', 'salam', 'assalam', 'aoa', 'how are you'])) {
    return createAnswer({
      title: 'Genie',
      text:
        'I am ready to help with Trend Genie: AI Analysis, RugShield, GenieSocials, GenieNova, pricing, rewards, onboarding, and crypto basics.',
      suggestions: ['What does Trend Genie app do?', 'Is the app free?', 'What is RugShield?'],
    });
  }

  return null;
}

function getAppLifecycleAnswer(question) {
  const q = normalize(question);
  const mentionsApp = hasAnyPhrase(q, APP_REFERENCE_TERMS);

  const asksAfterTrial = hasAnyPhrase(q, [
    'after 7 days',
    'after seven days',
    'after trial',
    'after the trial',
    'after free trial',
    'after the free trial',
    'what will happen after 7 days',
    'what happens after 7 days',
  ]);

  const asksNoSubscription = hasAllConcepts(q, [
    ['if', 'what'],
    ['dont', 'do not', 'not', 'without'],
    ['buy', 'pay', 'subscribe', 'subscription'],
    ['app', 'plan', 'monthly'],
  ]);

  const asksPlanOutcome = hasAllConcepts(q, [
    ['monthly', 'subscription', 'plan'],
    ['happen', 'happens'],
  ]);

  if (!mentionsApp && !asksAfterTrial) return null;

  if (asksAfterTrial || asksNoSubscription || asksPlanOutcome) {
    return {
      title: 'After the Free Trial',
      text:
        'After the 7-day free trial ends, the paid app features continue only if you subscribe to the $10 per month plan. If you do not subscribe, the full premium app experience should stop, while any limited free-tier ecosystem access may remain separate from premium features.',
      links: [
        { label: 'AI Signals App Page', href: '/ai-crypto-signals-app', external: false },
        { label: 'Download on Google Play', href: PLAY_STORE_URL, external: true },
      ],
      suggestions: ['How much does the app cost?', 'What features are in the app?', 'How do I get the app?'],
    };
  }

  return null;
}

function getAppTierAnswer(question) {
  const q = normalize(question);
  const mentionsAppOrPlan =
    hasAnyPhrase(q, APP_REFERENCE_TERMS) ||
    hasAnyPhrase(q, ['free tier', 'free version', 'premium', 'premium plan', 'monthly plan', 'subscription']);

  if (!mentionsAppOrPlan) return null;

  const asksFreeTier = hasAnyPhrase(q, [
    'free tier',
    'free version',
    'free features',
    'what is free',
    'what can i use for free',
    'what are free tier features',
    'what are the free tier features',
    'what features are free',
    'without subscription',
    'without plan',
  ]);

  const asksPremiumTier = hasAnyPhrase(q, [
    'premium features',
    'premium plan',
    'what is in premium',
    'what do i get in premium',
    'what features are in premium',
    'what does premium include',
    'what do i get after subscription',
  ]);

  if (asksFreeTier) {
    return {
      title: 'Free Tier vs Premium',
      text:
        'Trend Genie: AI Analysis starts with a 7-day free trial. After that, the full premium app experience requires the $10 per month plan. Outside premium access, you should expect more limited free-tier ecosystem access such as selected community, chat, or insight-style features rather than the full app feature set.',
      links: [
        { label: 'AI Signals App Page', href: '/ai-crypto-signals-app', external: false },
        { label: 'Download on Google Play', href: PLAY_STORE_URL, external: true },
      ],
      suggestions: ['What happens after 7 days?', 'How much does the app cost?', 'What features are in the app?'],
    };
  }

  if (asksPremiumTier) {
    return {
      title: 'Premium Access',
      text:
        'Premium is the full app experience. It follows the 7-day free trial and continues at $10 per month. Premium is the plan intended to unlock the complete product workflow compared with more limited free-tier access.',
      links: [
        { label: 'AI Signals App Page', href: '/ai-crypto-signals-app', external: false },
        { label: 'Download on Google Play', href: PLAY_STORE_URL, external: true },
      ],
      suggestions: ['What happens after 7 days?', 'How do I get the app?', 'What features are in the app?'],
    };
  }

  return null;
}

function getAppFeatureAnswer(question) {
  const q = normalize(question);
  const mentionsApp = hasAnyPhrase(q, APP_REFERENCE_TERMS);
  const asksFeatures = hasAnyPhrase(q, [
    'feature',
    'features',
    'what can it do',
    'what can the app do',
    'what does it include',
    'what is included',
    'signals work',
  ]);

  if (!mentionsApp || !asksFeatures) return null;

  return {
    title: 'App Features',
    text:
      'Trend Genie: AI Analysis includes real-time crypto trade setups with entry, TP1, TP2, and stop-loss, plus chart-image analysis, built-in AI coin analysis, crypto news, watchlists, and trader discussion features.',
    links: [
      { label: 'AI Signals App Page', href: '/ai-crypto-signals-app', external: false },
      { label: 'Download on Google Play', href: PLAY_STORE_URL, external: true },
    ],
    suggestions: ['Is the app free?', 'What happens after 7 days?', 'How do I get the app?'],
  };
}

function getProductKnowledge(question) {
  let bestProduct = null;
  let bestScore = 0;

  products.forEach((product) => {
    const anchors = PRODUCT_ANCHORS[product.key] || [];
    const hasStrongAnchor = anchors.some((anchor) => normalize(question).includes(normalize(anchor)));
    if (!hasStrongAnchor) return;

    const aliases = [
      product.name,
      product.shortName,
      product.primaryKeyword,
      ...(product.secondaryKeywords || []),
      ...(PRODUCT_ALIASES[product.key] || []),
    ];
    const score = scoreMatch(question, aliases);
    if (score > bestScore) {
      bestScore = score;
      bestProduct = product;
    }
  });

  if (!bestProduct || bestScore < 3) return null;

  return {
    title: bestProduct.name,
    text: `${bestProduct.overview} Best for: ${bestProduct.bestFor.slice(0, 2).join(' and ')}.`,
    links: [
      { label: 'Product Page', href: bestProduct.path, external: false },
      { label: 'Open Product', href: bestProduct.externalUrl, external: true },
    ],
    suggestions:
      bestProduct.key === 'android-app'
        ? ['How much does the app cost?', 'How do signals work?']
        : bestProduct.key === 'rugshield-web'
          ? ['Is RugShield free?', 'How does RugShield work?']
          : bestProduct.key === 'social'
            ? ['What is TGEN used for?', 'Is GenieSocials free?']
            : ['What can GenieNova do?', 'Which product should I start with?'],
  };
}

function getProductSpecificCommercialAnswer(question) {
  const q = normalize(question);
  const pricingWords = ['free', 'trial', 'price', 'pricing', 'cost', 'monthly', 'subscription', 'paid'];
  const hasCommercialIntent = pricingWords.some((word) => q.includes(word));

  if (!hasCommercialIntent) return null;

  const product = getProductKnowledge(question);
  if (!product) return null;

  if (q.includes('android') || q.includes('signals app') || q.includes('trend genie')) {
    return {
      title: 'Trend Genie: AI Analysis Pricing',
      text:
        'The Android app is not permanently free. It offers a 7-day free trial first, and after that it continues at $10 per month.',
      links: [
        { label: 'AI Signals App Page', href: '/ai-crypto-signals-app', external: false },
        { label: 'Download App', href: PLAY_STORE_URL, external: true },
      ],
      suggestions: ['What features are in the app?', 'How do signals work?'],
    };
  }

  if (q.includes('rugshield')) {
    return {
      title: 'RugShield Pricing',
      text:
        'RugShield is free to use. It is a read-only DeFi scanner and does not require a wallet connection for basic token checks.',
      links: [
        { label: 'RugShield Page', href: '/rugshield-defi-scanner', external: false },
        { label: 'Open RugShield', href: 'https://app.trendgenie.io', external: true },
      ],
      suggestions: ['What does RugShield check?', 'How does RugShield work?'],
    };
  }

  if (q.includes('social')) {
    return {
      title: 'GenieSocials Access',
      text:
        'GenieSocials is free to join. It is positioned around community participation, discussions, and TGEN-linked ecosystem rewards.',
      links: [{ label: 'Social Product Page', href: '/products/social', external: false }],
      suggestions: ['What is TGEN used for?', 'How do rewards work?'],
    };
  }

  if (q.includes('nova')) {
    return {
      title: 'GenieNova Status',
      text:
        'GenieNova is currently in testing, so it should be treated as an emerging product rather than a public fully-priced release.',
      links: [{ label: 'GenieNova Page', href: '/products/nova', external: false }],
      suggestions: ['What can GenieNova do?', 'Which product should I start with?'],
    };
  }

  return null;
}

function getDownloadAnswer(question) {
  const q = normalize(question);
  const downloadPatterns = [
    'how can i get this app',
    'how do i get this app',
    'get this app',
    'get app',
    'download app',
    'install app',
    'how can i download',
    'how do i download',
  ];

  const isDownloadIntent = downloadPatterns.some((pattern) => q.includes(pattern));
  const mentionsTrendGenie =
    q.includes('trend genie') ||
    q.includes('android') ||
    q.includes('signals app') ||
    q.includes('this app') ||
    q.includes('app');

  if (!isDownloadIntent || !mentionsTrendGenie) return null;

  return {
    title: 'Download Trend Genie: AI Analysis',
    text:
      'You can get the Android app directly from Google Play. Install it, create your account, and start with the 7-day free trial before the $10 per month plan begins.',
    links: [
      { label: 'Download on Google Play', href: PLAY_STORE_URL, external: true },
      { label: 'App Product Page', href: '/ai-crypto-signals-app', external: false },
    ],
    suggestions: ['Is the app free?', 'What features are in the app?'],
  };
}

function getTopicKnowledge(question) {
  let best = null;
  let bestScore = 0;
  TOPICS.forEach((topic) => {
    const score = scoreMatch(question, topic.keywords);
    if (score > bestScore) {
      best = topic;
      bestScore = score;
    }
  });
  return best && bestScore >= 2 ? best.build() : null;
}

function getCryptoBasics(question) {
  const q = normalize(question);
  if (!q) return null;

  if (q.includes('what is crypto') || q.includes('what is blockchain') || q.includes('what is defi')) {
    return {
      title: 'Crypto Basics',
      text:
        'Crypto assets run on blockchain networks. DeFi means using on-chain financial tools such as swaps, staking, and token trading without traditional intermediaries. Because DeFi is open and fast-moving, contract safety and risk management matter a lot.',
      suggestions: ['Explain RugShield in simple words', 'How should I manage risk?'],
    };
  }

  if (q.includes('risk management') || q.includes('position size') || q.includes('leverage')) {
    return {
      title: 'Risk Management Basics',
      text:
        'Use smaller position sizes, define your stop-loss before entry, avoid revenge trading, and do not over-leverage. A clean setup is still a bad trade if the risk is too large for your account size.',
      links: [{ label: 'Risk Insight', href: '/insights/risk-management-small-trading-accounts', external: false }],
      suggestions: ['How do signals work?', 'What does RugShield check?'],
    };
  }

  return null;
}

function buildFallbackAnswer() {
  return {
    title: 'I Can Help With',
    text:
      'I can answer professional questions about Trend Genie: AI Analysis, RugShield, GenieSocials, GenieNova, pricing, rewards, token utility, onboarding, and crypto basics such as DeFi safety or risk management.',
    links: [{ label: 'View All Products', href: '/products', external: false }],
    suggestions: QUICK_PROMPTS.slice(0, 4),
  };
}

function resolveAnswer(question, liveStats) {
  return (
    getDirectIntentAnswer(question) ||
    getLiveStatsAnswer(question, liveStats) ||
    getBroadIntentAnswer(question) ||
    getAppLifecycleAnswer(question) ||
    getAppTierAnswer(question) ||
    getAppFeatureAnswer(question) ||
    getDownloadAnswer(question) ||
    getProductSpecificCommercialAnswer(question) ||
    getTopicKnowledge(question) ||
    getProductKnowledge(question) ||
    getCryptoBasics(question) ||
    buildFallbackAnswer()
  );
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim());
}

function createMessage(role, payload) {
  return {
    id: `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    role,
    ...payload,
  };
}

const ChatBotWidget = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [liveStats, setLiveStats] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [pendingLeadCapture, setPendingLeadCapture] = useState(false);
  const [pendingQuestion, setPendingQuestion] = useState('');
  const [leadName, setLeadName] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [submittingLead, setSubmittingLead] = useState(false);
  const [messages, setMessages] = useState([
    createMessage('bot', {
      title: 'Trend Genie AI Assistant',
      text:
        'Ask me about Trend Genie: AI Analysis, RugShield, GenieSocials, GenieNova, pricing, rewards, or crypto basics. I will keep answers focused and product-aware.',
      suggestions: QUICK_PROMPTS.slice(0, 4),
    }),
  ]);
  const messagesEndRef = useRef(null);

  const canSend = useMemo(() => input.trim().length > 0, [input]);

  useEffect(() => {
    if (!messagesEndRef.current) return;
    messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, isTyping, open]);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'statistics', 'liveResults'), (snap) => {
      if (snap.exists()) setLiveStats(snap.data());
    });
    return () => unsub();
  }, []);

  const appendMessage = (role, payload) => {
    setMessages((prev) => [...prev, createMessage(role, payload)]);
  };

  const answerQuestion = (question) => {
    const answer = resolveAnswer(question, liveStats);
    const shouldCaptureLead = answer.leadCapture || normalize(question).includes('contact team');

    setIsTyping(true);
    window.setTimeout(() => {
      appendMessage('bot', answer);
      setIsTyping(false);
      setPendingLeadCapture(Boolean(shouldCaptureLead));
      setPendingQuestion(shouldCaptureLead ? question : '');
    }, 420);
  };

  const handleSend = () => {
    const question = input.trim();
    if (!question) return;
    appendMessage('user', { text: question });
    setInput('');
    answerQuestion(question);
  };

  const handlePrompt = (prompt) => {
    appendMessage('user', { text: prompt });
    answerQuestion(prompt);
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    const name = leadName.trim();
    const email = leadEmail.trim();

    if (!name || !email) return;
    if (!isValidEmail(email)) {
      appendMessage('bot', { title: 'Email Check', text: 'Please enter a valid email address so the team can reply properly.' });
      return;
    }

    setSubmittingLead(true);

    try {
      const existing = JSON.parse(localStorage.getItem('tg_chatbot_leads') || '[]');
      const updated = [
        ...existing,
        {
          name,
          email,
          question: pendingQuestion,
          source: 'website_chatbot',
          createdAt: new Date().toISOString(),
        },
      ];
      localStorage.setItem('tg_chatbot_leads', JSON.stringify(updated));
      appendMessage('bot', {
        title: 'Request Saved',
        text: 'Your support request has been saved locally in this browser. You can also email support@trendgenie.io for a direct follow-up.',
        links: [{ label: 'Email Support', href: 'mailto:support@trendgenie.io', external: true }],
      });
    } catch (error) {
      appendMessage('bot', {
        title: 'Request Received',
        text: 'I could not store the request locally, but you can still contact the team directly at support@trendgenie.io.',
        links: [{ label: 'Email Support', href: 'mailto:support@trendgenie.io', external: true }],
      });
    } finally {
      setSubmittingLead(false);
      setPendingLeadCapture(false);
      setPendingQuestion('');
      setLeadName('');
      setLeadEmail('');
    }
  };

  return (
    <Wrap>
      <AnimatePresence>
        {open && (
          <Panel
            initial={{ opacity: 0, y: 18, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.96 }}
            transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
          >
            <PanelGlow />
            <PanelScan />
            <Header>
              <HeaderMeta>
                <AiIconWrap>
                  <BrandLogo src={logoImage} alt="Trend Genie" />
                </AiIconWrap>
                <div>
                  <Title>Trend Genie Assistant</Title>
                  <StatusRow>
                    <StatusDot />
                    Product-aware assistant
                  </StatusRow>
                </div>
              </HeaderMeta>
              <HeaderActions>
                <CollapseBtn type="button" onClick={() => setOpen(false)} aria-label="Close AI assistant">
                  <FaChevronDown />
                </CollapseBtn>
              </HeaderActions>
            </Header>

            <QuickBar>
              {QUICK_PROMPTS.slice(0, 3).map((prompt) => (
                <QuickChip type="button" key={prompt} onClick={() => handlePrompt(prompt)}>
                  {prompt}
                </QuickChip>
              ))}
            </QuickBar>

            <Messages>
              {messages.map((message) => (
                <Bubble key={message.id} $role={message.role}>
                  {message.title && (
                    message.role === 'bot' ? (
                      <BrandedBubbleTitle>
                        <BrandedBubbleLogo src={logoImage} alt="Trend Genie" />
                        <span>Genie</span>
                      </BrandedBubbleTitle>
                    ) : (
                      <BubbleTitle $role={message.role}>{message.title}</BubbleTitle>
                    )
                  )}
                  {message.text && <BubbleText $role={message.role}>{message.text}</BubbleText>}
                  {!!message.links?.length && (
                    <LinkStack>
                      {message.links.map((link) =>
                        link.external ? (
                          <BubbleLink key={link.label} href={link.href} target="_blank" rel="noopener noreferrer">
                            {link.label} <FaExternalLinkAlt />
                          </BubbleLink>
                        ) : (
                          <BubbleLink key={link.label} href={link.href}>
                            {link.label} <FaArrowRight />
                          </BubbleLink>
                        )
                      )}
                    </LinkStack>
                  )}
                  {!!message.suggestions?.length && (
                    <SuggestionRow>
                      {message.suggestions.slice(0, 3).map((suggestion) => (
                        <SuggestionChip key={suggestion} type="button" onClick={() => handlePrompt(suggestion)}>
                          {suggestion}
                        </SuggestionChip>
                      ))}
                    </SuggestionRow>
                  )}
                </Bubble>
              ))}

              {isTyping && (
                <Bubble $role="bot">
                  <ThinkingTitle>
                    <ThinkingLogo src={logoImage} alt="Trend Genie" />
                    <span>Genie Thinking</span>
                  </ThinkingTitle>
                  <TypingRow>
                    <TypingDot />
                    <TypingDot />
                    <TypingDot />
                  </TypingRow>
                </Bubble>
              )}
              <div ref={messagesEndRef} />
            </Messages>

            <AnimatePresence>
              {pendingLeadCapture && (
                <LeadForm
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.2 }}
                  onSubmit={handleLeadSubmit}
                >
                  <LeadTitle><FaEnvelope /> Contact Support</LeadTitle>
                  <LeadText>Share your details if you want the team to follow up on this question.</LeadText>
                  <LeadInput
                    type="text"
                    placeholder="Your name"
                    value={leadName}
                    onChange={(e) => setLeadName(e.target.value)}
                    required
                  />
                  <LeadInput
                    type="email"
                    placeholder="Your email"
                    value={leadEmail}
                    onChange={(e) => setLeadEmail(e.target.value)}
                    required
                  />
                  <LeadSubmit type="submit" disabled={submittingLead}>
                    {submittingLead ? 'Saving...' : 'Send to Support'}
                  </LeadSubmit>
                </LeadForm>
              )}
            </AnimatePresence>

            <Composer>
              <ComposerInput
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about products, pricing, DeFi safety, TGEN, or crypto basics..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
              />
              <SendBtn type="button" onClick={handleSend} disabled={!canSend || isTyping}>
                <FaPaperPlane />
              </SendBtn>
            </Composer>
          </Panel>
        )}
      </AnimatePresence>

      <Toggle type="button" onClick={() => setOpen((value) => !value)} $open={open}>
        <ToggleOrb />
        <ToggleInner>
          <ToggleIcon>
            <ToggleLogo src={logoImage} alt="Trend Genie" />
          </ToggleIcon>
          <ToggleText>
            <strong>{open ? 'Hide Genie' : 'Ask Genie'}</strong>
            <span>Product assistant</span>
          </ToggleText>
        </ToggleInner>
      </Toggle>
    </Wrap>
  );
};

export default ChatBotWidget;

const pulseRing = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.9; }
  50% { transform: scale(1.08); opacity: 0.45; }
`;

const floatGlow = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

const scanLine = keyframes`
  0% { transform: translateX(-120%); }
  100% { transform: translateX(320%); }
`;

const typing = keyframes`
  0%, 80%, 100% { opacity: 0.3; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-3px); }
`;

const Wrap = styled.div`
  position: fixed;
  right: 18px;
  bottom: 18px;
  z-index: 99999;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.85rem;
`;

const Toggle = styled.button`
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(41, 171, 226, 0.18);
  border-radius: 999px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.96), rgba(244, 249, 253, 0.98));
  color: var(--text-color);
  padding: 0.8rem 1rem 0.8rem 0.88rem;
  cursor: pointer;
  box-shadow: 0 16px 40px rgba(18, 37, 52, 0.12), 0 0 22px rgba(41, 171, 226, 0.08);
  backdrop-filter: blur(16px);
  min-width: 160px;
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(41, 171, 226, 0.34);
    box-shadow: 0 20px 44px rgba(18, 37, 52, 0.16), 0 0 24px rgba(41, 171, 226, 0.12);
  }
`;

const ToggleOrb = styled.span`
  position: absolute;
  width: 86px;
  height: 86px;
  right: -20px;
  top: -24px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(41, 171, 226, 0.32), transparent 70%);
  filter: blur(10px);
  animation: ${floatGlow} 3.2s ease-in-out infinite;
  pointer-events: none;
`;

const ToggleInner = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.72rem;
`;

const ToggleIcon = styled.span`
  width: 36px;
  height: 36px;
  border-radius: 12px;
  background: linear-gradient(135deg, #e8f6fd, #d8eef9);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 4px rgba(41, 171, 226, 0.08);
`;

const ToggleLogo = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
`;

const ToggleText = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  strong {
    font-size: 0.9rem;
    line-height: 1.1;
    color: var(--text-color);
  }

  span {
    font-size: 0.7rem;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.09em;
  }
`;

const Panel = styled(motion.div)`
  position: relative;
  width: min(430px, calc(100vw - 22px));
  max-height: min(78vh, 720px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 26px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(246, 250, 253, 0.98));
  border: 1px solid rgba(41, 171, 226, 0.14);
  box-shadow: 0 28px 80px rgba(21, 39, 54, 0.16), 0 0 0 1px rgba(255, 255, 255, 0.4) inset;
  backdrop-filter: blur(18px);
`;

const PanelGlow = styled.div`
  position: absolute;
  inset: auto auto -90px -70px;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(41, 171, 226, 0.18), transparent 70%);
  filter: blur(12px);
  pointer-events: none;
`;

const PanelScan = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 90px;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(41, 171, 226, 0.14), transparent);
  animation: ${scanLine} 4.8s linear infinite;
  pointer-events: none;
`;

const Header = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
  padding: 1rem 1rem 0.8rem;
  border-bottom: 1px solid rgba(41, 171, 226, 0.1);
`;

const HeaderMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

const AiIconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 15px;
  background: linear-gradient(135deg, rgba(41, 171, 226, 0.12), rgba(16, 185, 129, 0.08));
  border: 1px solid rgba(41, 171, 226, 0.18);
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const BrandLogo = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
`;

const Title = styled.div`
  font-size: 0.94rem;
  font-weight: 800;
  color: var(--text-color);
`;

const StatusRow = styled.div`
  margin-top: 0.2rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted);
`;

const StatusDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.55);
  animation: ${pulseRing} 1.7s ease-in-out infinite;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
`;

const CollapseBtn = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 11px;
  border: 1px solid rgba(41, 171, 226, 0.12);
  background: rgba(41, 171, 226, 0.05);
  color: var(--text-secondary-color);
  cursor: pointer;
`;

const QuickBar = styled.div`
  display: flex;
  gap: 0.45rem;
  padding: 0.7rem 1rem 0;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const QuickChip = styled.button`
  border: 1px solid rgba(41, 171, 226, 0.14);
  background: rgba(41, 171, 226, 0.04);
  color: var(--text-secondary-color);
  border-radius: 999px;
  padding: 0.45rem 0.75rem;
  white-space: nowrap;
  font-size: 0.72rem;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: rgba(41, 171, 226, 0.12);
    border-color: rgba(41, 171, 226, 0.36);
    transform: translateY(-1px);
  }
`;

const Messages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  min-height: 280px;
`;

const Bubble = styled.div`
  max-width: 92%;
  align-self: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
  padding: 0.9rem;
  border-radius: 18px;
  background: ${({ $role }) =>
    $role === 'user'
      ? 'linear-gradient(135deg, rgba(41, 171, 226, 0.18), rgba(182, 227, 248, 0.35))'
      : 'rgba(255, 255, 255, 0.76)'};
  border: 1px solid
    ${({ $role }) => ($role === 'user' ? 'rgba(41, 171, 226, 0.24)' : 'rgba(41, 171, 226, 0.1)')};
  box-shadow: ${({ $role }) =>
    $role === 'user' ? '0 12px 30px rgba(41, 171, 226, 0.08)' : '0 10px 24px rgba(18, 37, 52, 0.08)'};
`;

const BubbleTitle = styled.div`
  font-size: 0.78rem;
  font-weight: 800;
  color: ${({ $role }) => ($role === 'user' ? '#0d4f73' : '#176c9a')};
  margin-bottom: 0.35rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const BrandedBubbleTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.78rem;
  font-weight: 800;
  color: #176c9a;
  margin-bottom: 0.35rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const BrandedBubbleLogo = styled.img`
  width: 16px;
  height: 16px;
  object-fit: contain;
`;

const BubbleText = styled.div`
  color: ${({ $role }) => ($role === 'user' ? '#14364d' : 'var(--text-secondary-color)')};
  font-size: 0.88rem;
  line-height: 1.72;
  white-space: pre-wrap;
`;

const ThinkingTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.78rem;
  font-weight: 800;
  color: #176c9a;
  margin-bottom: 0.35rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const ThinkingLogo = styled.img`
  width: 18px;
  height: 18px;
  object-fit: contain;
`;

const LinkStack = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin-top: 0.8rem;
`;

const BubbleLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.5rem 0.75rem;
  border-radius: 12px;
  text-decoration: none;
  background: rgba(41, 171, 226, 0.12);
  border: 1px solid rgba(41, 171, 226, 0.18);
  color: #176c9a;
  font-size: 0.78rem;
  font-weight: 700;
`;

const SuggestionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-top: 0.8rem;
`;

const SuggestionChip = styled.button`
  border: 1px solid rgba(41, 171, 226, 0.12);
  background: rgba(41, 171, 226, 0.05);
  color: var(--text-secondary-color);
  border-radius: 999px;
  padding: 0.38rem 0.65rem;
  font-size: 0.72rem;
  cursor: pointer;
`;

const TypingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  height: 20px;
`;

const TypingDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #29abe2;
  animation: ${typing} 1.1s infinite ease-in-out;

  &:nth-child(2) {
    animation-delay: 0.14s;
  }

  &:nth-child(3) {
    animation-delay: 0.28s;
  }
`;

const LeadForm = styled(motion.form)`
  margin: 0 1rem 0.9rem;
  padding: 0.95rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(41, 171, 226, 0.16);
  display: grid;
  gap: 0.55rem;
`;

const LeadTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  color: var(--text-color);
  font-size: 0.82rem;
  font-weight: 800;
`;

const LeadText = styled.div`
  color: var(--text-secondary-color);
  font-size: 0.76rem;
  line-height: 1.6;
`;

const LeadInput = styled.input`
  width: 100%;
  border: 1px solid rgba(41, 171, 226, 0.14);
  border-radius: 13px;
  padding: 0.72rem 0.8rem;
  font-size: 0.84rem;
  color: var(--text-color);
  background: white;
  outline: none;
`;

const LeadSubmit = styled.button`
  border: none;
  border-radius: 13px;
  padding: 0.75rem 0.85rem;
  font-size: 0.84rem;
  font-weight: 800;
  color: #fff;
  background: linear-gradient(135deg, #29abe2, #176c9a);
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.65 : 1)};
`;

const Composer = styled.div`
  padding: 0.9rem 1rem 1rem;
  border-top: 1px solid rgba(41, 171, 226, 0.1);
  display: flex;
  gap: 0.6rem;
  align-items: center;
`;

const ComposerInput = styled.input`
  flex: 1;
  border: 1px solid rgba(41, 171, 226, 0.14);
  border-radius: 15px;
  padding: 0.86rem 0.9rem;
  font-size: 0.86rem;
  outline: none;
  color: var(--text-color);
  background: white;

  &::placeholder {
    color: var(--text-muted);
  }
`;

const SendBtn = styled.button`
  width: 46px;
  height: 46px;
  border-radius: 14px;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: linear-gradient(135deg, #29abe2, #176c9a);
  box-shadow: 0 10px 28px rgba(41, 171, 226, 0.24);
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.55 : 1)};
`;
